import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { notificationService } from '../services/api';
import { Mail, Loader2, Inbox, CheckCheck } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { key: 'all', label: 'All' },
  { key: 'application', label: 'Application' },
  { key: 'onboarding', label: 'Onboarding' },
  { key: 'operational', label: 'Operational' },
  { key: 'settlement', label: 'Settlement' },
  { key: 'general', label: 'General' },
];

const DriverMessagesPage = () => {
  const [loading, setLoading] = useState(true);
  const [available, setAvailable] = useState(true);
  const [data, setData] = useState({ items: [], unread_count: 0 });
  const [filter, setFilter] = useState('all');

  const load = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/driver-messages`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 403) { setAvailable(false); return; }
      if (!res.ok) throw new Error('load failed');
      setData(await res.json());
      setAvailable(true);
    } catch { /* keep prior */ } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const markRead = async (id) => {
    try {
      await notificationService.markAsRead(id);
      setData((d) => ({
        ...d,
        items: d.items.map((i) => (i.id === id ? { ...i, is_read: true } : i)),
        unread_count: Math.max(0, d.unread_count - 1),
      }));
    } catch { /* ignore */ }
  };

  const items = filter === 'all' ? data.items : data.items.filter((i) => i.category === filter);

  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto space-y-6" data-testid="driver-messages-page">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Inbox className="w-7 h-7 text-blue-600" />
            Messages &amp; Notifications
          </h1>
          <p className="text-gray-600 mt-1" data-testid="driver-messages-unread">
            {data.unread_count} unread
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-12" data-testid="driver-messages-loading">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : !available ? (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6" data-testid="driver-messages-unavailable">
            <p className="text-gray-700">Messages center is not currently available.</p>
          </div>
        ) : (
          <>
            <div className="flex flex-wrap gap-2" data-testid="driver-messages-filters">
              {CATEGORIES.map((c) => (
                <button
                  key={c.key}
                  type="button"
                  onClick={() => setFilter(c.key)}
                  data-testid={`driver-messages-filter-${c.key}`}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                    filter === c.key ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {c.label}
                </button>
              ))}
            </div>

            {items.length === 0 ? (
              <div className="text-center py-12 text-gray-400" data-testid="driver-messages-empty">
                <Mail className="w-10 h-10 mx-auto mb-2" />
                <p>No messages yet.</p>
              </div>
            ) : (
              <div className="space-y-2" data-testid="driver-messages-list">
                {items.map((m) => (
                  <div
                    key={m.id}
                    data-testid={`driver-message-${m.id}`}
                    className={`rounded-lg border p-4 ${m.is_read ? 'bg-white border-gray-200' : 'bg-blue-50 border-blue-200'}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] uppercase tracking-wider text-gray-400">{m.category}</span>
                          {!m.is_read && <span className="w-2 h-2 rounded-full bg-blue-600" />}
                        </div>
                        <p className="font-medium text-gray-900 mt-1">{m.title}</p>
                        <p className="text-sm text-gray-600 mt-0.5">{m.message}</p>
                      </div>
                      {!m.is_read && (
                        <button
                          type="button"
                          onClick={() => markRead(m.id)}
                          data-testid={`driver-message-markread-${m.id}`}
                          className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline whitespace-nowrap"
                        >
                          <CheckCheck className="w-4 h-4" /> Mark read
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default DriverMessagesPage;
